import { ComponentType, useCallback, useMemo, useState } from "react";

import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import { Popup, ToolbarItem } from "devextreme-react/popup";

import { IHttpClient } from "@ezhou/common";

import { getFormulaSchema, validateFormula } from "../../services/formulaService";

import { FormulaEditor } from "./FormulaEditor";

export interface FormulaEditCellParams {
  httpClient: IHttpClient;
  /** Formula context key, e.g. "transaction". */
  contextKey: string;
  /**
   * Names of the root objects available to this field (a subset of the context's universe).
   * Restricts autocomplete/palette and is sent to server validation. When omitted, all of the
   * context's root objects are used.
   */
  rootObjects?: string[];
  /** Run server-side compile validation as the user types. Defaults to true. */
  enableServerValidation?: boolean;
  /** Editor height (CSS value). */
  height?: string;
}

/**
 * Subset of the DataGrid `editCellTemplate` model (`ColumnEditCellTemplateData`) that the formula
 * editor needs: the current editing value and a callback to write changes back to the grid's
 * editing controller.
 */
interface FormulaEditCellInfo {
  value?: string;
  setValue?: (value: unknown) => void;
}

interface FormulaEditCellProps extends FormulaEditCellParams {
  cell: FormulaEditCellInfo;
}

/**
 * Bridges the reusable {@link FormulaEditor} to a DevExtreme DataGrid editing cell. While editing,
 * the value is owned by the grid's editing controller (the popup form's `formData` stays empty), so
 * the initial value is taken from `cell.value` and changes are pushed back via `cell.setValue`.
 *
 * The cell renders as a compact read-only text box with a pencil button; clicking it opens the rich
 * formula editor (autocomplete, field palette, validation) in a dialog with Save/Cancel.
 */
const FormulaEditCell = ({
  cell,
  httpClient,
  contextKey,
  rootObjects,
  enableServerValidation,
  height,
}: FormulaEditCellProps) => {
  const serverValidation = enableServerValidation ?? true;

  const [value, setValue] = useState<string>(typeof cell.value === "string" ? cell.value : "");
  const [draft, setDraft] = useState<string>(value);
  const [editorVisible, setEditorVisible] = useState(false);

  const loadSchema = useCallback(
    () => getFormulaSchema(httpClient, contextKey),
    [httpClient, contextKey],
  );

  const validate = useMemo(
    () =>
      serverValidation
        ? (expression: string) => validateFormula(httpClient, contextKey, expression, rootObjects)
        : undefined,
    [serverValidation, httpClient, contextKey, rootObjects],
  );

  const openEditor = useCallback(() => {
    setDraft(value);
    setEditorVisible(true);
  }, [value]);

  const cancelEdit = useCallback(() => {
    setEditorVisible(false);
  }, []);

  const saveEdit = useCallback(() => {
    setValue(draft);
    cell.setValue?.(draft);
    setEditorVisible(false);
  }, [draft, cell]);

  const cancelButtonOptions = useMemo(
    () => ({
      text: "Cancel",
      stylingMode: "text" as const,
      onClick: cancelEdit,
    }),
    [cancelEdit],
  );

  const saveButtonOptions = useMemo(
    () => ({
      text: "Save",
      type: "default" as const,
      stylingMode: "contained" as const,
      onClick: saveEdit,
    }),
    [saveEdit],
  );

  return (
    <div className="formula-field">
      <Button
        className="formula-field__edit"
        icon="edit"
        stylingMode="text"
        hint="Edit formula"
        onClick={openEditor}
      />
      <TextBox
        className="formula-field__value"
        value={value}
        readOnly
        stylingMode="outlined"
        placeholder="Enter a C# expression…"
        hint={value || undefined}
      />

      <Popup
        visible={editorVisible}
        onHiding={cancelEdit}
        title="Edit Formula"
        showCloseButton
        dragEnabled
        hideOnOutsideClick={false}
        width="60%"
        height="auto"
        maxHeight="80%"
      >
        <ToolbarItem toolbar="bottom" location="after" widget="dxButton" options={cancelButtonOptions} />
        <ToolbarItem toolbar="bottom" location="after" widget="dxButton" options={saveButtonOptions} />
        {editorVisible && (
          <FormulaEditor
            value={draft}
            onValueChange={setDraft}
            loadSchema={loadSchema}
            validate={validate}
            rootObjects={rootObjects}
            height={height ?? "280px"}
          />
        )}
      </Popup>
    </div>
  );
};

/**
 * Produces a component for a DataGrid column's `editCellComponent`, rendering the reusable formula
 * editor while keeping the grid's editing binding intact. Assign it to the formula columns and give
 * the corresponding form items a `colSpan` via `editorItemOptions`.
 */
export function createFormulaEditCellComponent(
  params: FormulaEditCellParams,
): ComponentType<{ data: FormulaEditCellInfo }> {
  return function FormulaEditCellRenderer(rendererProps: { data: FormulaEditCellInfo }) {
    return <FormulaEditCell cell={rendererProps.data} {...params} />;
  };
}
